import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";
import axios from "axios";
import type { AxiosError } from "axios";
import useSWR from "swr";
import {
  PrimaryButtonMediumV2,
  TertiaryButtonMediumV2,
} from "../../../../../components/Buttons/Buttons";
import {
  CollapseIcon,
  ExpandIcon,
} from "../../../../../components/Icons/Icons";
import { Loader } from "../../../../../components/Loader/Loader";
import { ErrorPlaceholder } from "../../../../../components/Error";
import { PageTitle } from "../../../../../layout/portalPageLayout";
import { Breadcrumb } from "../../../../../components/ProductsLayout/SharedProductsLayout";
import { endpoints } from "../../../../../endpoints";
import type { PIMProduct } from "../../../../../types/types.PIM";
import type { ProfileComparisonConfigsResponse } from "../../../../../types/types";
import { useAuthContext } from "../../../../../components/Auth";
import { useRoutePath } from "../../../../../util/Routing";
import { makeUrlWithParams, useStoreState } from "../../../../../util/util";
import { CompareProductHeader } from "./CompareProductHeader";
import { CompareGroupSection } from "./CompareGroupSection";
import { CompareCollectionSection } from "./CompareCollectionSection";
import { ExportCompareModal } from "./ExportCompareModal";
import type { CompareConfigItem } from "./CompareProducts.util";
import {
  getCompareSections,
  MAX_COMPARE_PRODUCTS,
  MIN_COMPARE_PRODUCTS,
  SESSION_SELECTED_ROWS_KEY,
} from "./CompareProducts.util";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.resolve("spacing_xl")};
  min-height: 100%;
  background: ${({ theme }) => theme.resolve("color_neutral_white")};
`;

const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
  margin-bottom: ${({ theme }) => theme.resolve("spacing_xl")};
`;

const ExpandControls = styled.div`
  display: flex;
  align-items: center;
  margin: ${({ theme }) => theme.resolve("spacing_lg")} 0;

  button {
    display: inline-flex;
    align-items: center;
    gap: ${({ theme }) => theme.resolve("spacing_sm")};
  }
`;

const Sections = styled.div`
  display: flex;
  flex-direction: column;
`;

function parseProductNumbers(search: string): string[] {
  const params = new URLSearchParams(search);
  const raw = params.get("products") || "";
  return [
    ...new Set(
      raw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    ),
  ].slice(0, MAX_COMPARE_PRODUCTS);
}

async function fetchProduct(
  tenantId: string,
  productNumber: string,
  returnStaged: boolean
): Promise<PIMProduct> {
  const url = makeUrlWithParams(
    endpoints.v2_tenants_id_pim_products_id(tenantId, productNumber),
    {
      use_database: "true",
      return_staged: returnStaged ? "true" : "false",
    }
  );
  const { data } = await axios.get<PIMProduct>(url);
  return data;
}

export function CompareProductsPage() {
  const { t } = useTranslation();
  const { adminPath } = useRoutePath();
  const { tenant_id } = useStoreState();
  const history = useHistory();
  const location = useLocation();
  const { hasPermission } = useAuthContext();
  const returnStaged =
    hasPermission("modify_products") || hasPermission("publish_products");

  const productNumbers = useMemo(
    () => parseProductNumbers(location.search),
    [location.search]
  );

  const [products, setProducts] = useState<PIMProduct[] | null>(null);
  const [error, setError] = useState<AxiosError | null>(null);
  const [loading, setLoading] = useState(false);
  const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>({});
  const [allExpanded, setAllExpanded] = useState(true);
  const [showExportModal, setShowExportModal] = useState(false);

  const templateId = products?.[0]?.product_schema?.template_id ?? null;
  const { data: comparisonConfigs } = useSWR<ProfileComparisonConfigsResponse>(
    tenant_id && templateId
      ? endpoints.v2_tenants_id_pim_templates_id_settings_profileComparison(
          tenant_id,
          templateId
        )
      : null
  );
  const configsLoaded = !templateId || comparisonConfigs != null;

  const compareConfigItems: CompareConfigItem[] = useMemo(
    () =>
      (comparisonConfigs?.data ?? []).map((row) => ({
        target_type: row.target_type,
        target_id: row.target_id,
        target_name: row.target_name,
        target_display_name: row.target_display_name,
        key_attribute_name: row.key_attribute_name,
      })),
    [comparisonConfigs]
  );

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!tenant_id || productNumbers.length < MIN_COMPARE_PRODUCTS) {
        setProducts(null);
        setError(null);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        // Sequential fetch in URL order so each column is bound to the
        // matching product response (avoids shared-cache / identity issues).
        const results: PIMProduct[] = [];
        for (const pn of productNumbers) {
          results.push(await fetchProduct(tenant_id, pn, returnStaged));
        }
        if (!cancelled) {
          setProducts(results);
        }
      } catch (err) {
        if (!cancelled) {
          setProducts(null);
          setError(err as AxiosError);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [tenant_id, productNumbers, returnStaged]);

  const sections = useMemo(
    () =>
      products?.length
        ? getCompareSections(products[0], products, compareConfigItems)
        : [],
    [products, compareConfigItems]
  );

  useEffect(() => {
    if (!sections.length) return;
    const next: Record<string, boolean> = {};
    for (const section of sections) {
      next[section.id] = true;
    }
    setExpandedMap(next);
    setAllExpanded(true);
  }, [sections]);

  const updateUrlProducts = useCallback(
    (nextNumbers: string[]) => {
      const params = new URLSearchParams();
      params.set("products", nextNumbers.join(","));
      history.replace(`${adminPath}/pim/compare-products?${params.toString()}`);
      sessionStorage.setItem(
        SESSION_SELECTED_ROWS_KEY,
        JSON.stringify(nextNumbers)
      );
    },
    [adminPath, history]
  );

  const handleRemove = (productNumber: string) => {
    const next = productNumbers.filter((pn) => pn !== productNumber);
    if (next.length < MIN_COMPARE_PRODUCTS) {
      history.push(`${adminPath}/pim/products`);
      return;
    }
    updateUrlProducts(next);
  };

  const toggleSection = (id: string) => {
    setExpandedMap((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      setAllExpanded(Object.values(next).every(Boolean));
      return next;
    });
  };

  const handleExpandCollapseAll = () => {
    const nextExpanded = !allExpanded;
    const next: Record<string, boolean> = {};
    for (const section of sections) {
      next[section.id] = nextExpanded;
    }
    setExpandedMap(next);
    setAllExpanded(nextExpanded);
  };

  const getProductHref = (product: PIMProduct) =>
    `${adminPath}/pim/products/${product.product_number || product.id}`;

  if (productNumbers.length < MIN_COMPARE_PRODUCTS) {
    return (
      <PageContainer>
        <ErrorPlaceholder
          message={t("Select at least 2 products to compare")}
          showBackButton
          backUrl={`${adminPath}/pim/products`}
        />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <ErrorPlaceholder
          message={t("Could not load products for comparison")}
          showBackButton
          backUrl={`${adminPath}/pim/products`}
        />
      </PageContainer>
    );
  }

  if (loading || !products || !configsLoaded) {
    return (
      <PageContainer>
        <Loader isLoading />
      </PageContainer>
    );
  }

  const hasComparisonConfig =
    (comparisonConfigs?.count ?? comparisonConfigs?.data?.length ?? 0) > 0;
  if (!hasComparisonConfig) {
    return (
      <PageContainer>
        <ErrorPlaceholder
          message={t(
            "No groups or collections are configured for comparison on this template"
          )}
          showBackButton
          backUrl={`${adminPath}/pim/products`}
        />
      </PageContainer>
    );
  }

  const templateIds = new Set(
    products.map((p) => p.product_schema.template_id)
  );
  if (templateIds.size > 1) {
    return (
      <PageContainer>
        <ErrorPlaceholder
          message={t("Products must be of the same template to compare")}
          showBackButton
          backUrl={`${adminPath}/pim/products`}
        />
      </PageContainer>
    );
  }

  const uniqueProductIds = new Set(products.map((p) => p.id));
  if (uniqueProductIds.size !== products.length) {
    return (
      <PageContainer>
        <ErrorPlaceholder
          message={t("Could not load products for comparison")}
          showBackButton
          backUrl={`${adminPath}/pim/products`}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Breadcrumb>
        <Link to={`${adminPath}/pim/products`}>{t("Products")}</Link>
        {" / "}
        <span>{t("Compare Products")}</span>
      </Breadcrumb>
      <TitleRow>
        <PageTitle>{t("Compare Products")}</PageTitle>
        <PrimaryButtonMediumV2 onClick={() => setShowExportModal(true)}>
          {t("Export")}
        </PrimaryButtonMediumV2>
      </TitleRow>

      <CompareProductHeader
        products={products}
        getProductHref={getProductHref}
        onRemove={handleRemove}
        canRemove
      />

      <ExpandControls>
        <TertiaryButtonMediumV2 onClick={handleExpandCollapseAll}>
          {allExpanded ? (
            <>
              <CollapseIcon /> {t("Collapse All")}
            </>
          ) : (
            <>
              <ExpandIcon /> {t("Expand All")}
            </>
          )}
        </TertiaryButtonMediumV2>
      </ExpandControls>

      <Sections>
        {sections.map((section) =>
          section.kind === "group" ? (
            <CompareGroupSection
              key={section.id}
              groupName={section.name}
              displayName={section.display_name}
              products={products}
              expanded={!!expandedMap[section.id]}
              onToggle={() => toggleSection(section.id)}
            />
          ) : (
            <CompareCollectionSection
              key={section.id}
              collectionName={section.name}
              displayName={section.display_name}
              products={products}
              expanded={!!expandedMap[section.id]}
              onToggle={() => toggleSection(section.id)}
              keyAttributeName={section.keyAttributeName}
            />
          )
        )}
      </Sections>

      <ExportCompareModal
        show={showExportModal}
        onClose={() => setShowExportModal(false)}
        products={products}
        sections={sections}
        entityName="product"
      />
    </PageContainer>
  );
}
